import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import type { SignedInAccount } from "../../api/accountsApi";
import { appPaths } from "../../shared/routing/appPaths";
import PersonAvatar from "../../ui/avatars/PersonAvatar";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSession } from "../../session/sessionContext";
import { useWorkspace } from "../../shell/workspace";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import DefinitionListItem from "../../ui/layout/DefinitionListItem";
import Chip from "../../ui/feedback/Chip";
import AvatarSettings from "./avatar/AvatarSettings";
import ExternalConnections from "./connections/ExternalConnections";

/** `/profile`: the account and its outside connections. Personal preferences are a profile page of their own (F79). */
export default function ProfilePage() {
  const { session } = useSession();
  return (
    <div className="app-card w-full max-w-2xl p-6 shadow-sm">
      {session.status === "signedIn" ? <AccountDetails account={session.session.account} /> : null}
      {session.status === "signedIn" ? <ExternalConnections /> : null}
    </div>
  );
}

function AccountDetails({ account }: { account: SignedInAccount }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useSession();
  const signOutSubmission = useSubmission();
  const { confirmLeave } = useWorkspace();

  return (
    <>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <PersonAvatar userId={account.userId} name={account.displayName} size="lg" />
          <h1 className="app-title text-xl font-semibold">{t("profile.title")}</h1>
        </div>
        <AppButton
          onClick={() => {
            // Every tab's unsaved drafts are offered for saving first, as the account menu does.
            confirmLeave(() => {
              signOutSubmission.submit(async () => {
                await signOut();
                await navigate(appPaths.login());
              });
            });
          }}
          variant="dangerSolid"
          size="sm"
          disabled={signOutSubmission.state.status === "submitting"}
        >
          {t("profile.logout")}
        </AppButton>
      </div>
      {signOutSubmission.state.status === "failed" ? (
        <ErrorNotice error={signOutSubmission.state.error} className="mb-4" />
      ) : null}

      <p className="app-text-muted mb-4 text-sm">
        {t("profile.personalPermissions")}{" "}
        <Link to={appPaths.profileConnectors()} className="app-link underline">{t("profile.pages.connectors")}</Link>
      </p>
      <dl className="app-divide-y">
        <DefinitionListItem term={t("profile.avatar.title")}>
          <AvatarSettings account={account} />
        </DefinitionListItem>
        <DefinitionListItem term={t("profile.displayName")}>{account.displayName}</DefinitionListItem>
        <DefinitionListItem term={t("profile.email")}>{account.email}</DefinitionListItem>
        <DefinitionListItem term={t("profile.accountRole")}>
          <Chip className="font-medium">
            {t(`profile.accountRoles.${account.accountRole}`)}
          </Chip>
        </DefinitionListItem>
      </dl>
    </>
  );
}
